import React, { lazy, Suspense, useCallback, useEffect, useRef } from 'react'
import { Trans } from 'react-i18next'
import { Skeleton } from '@fs/zion-ui'
import { trackLink } from '@fs/zion-analytics'
import { useUser } from '@fs/zion-user'
import { type Player as BrightcovePlayerType, type MediaError } from '@fs/zion-video'
import { type YouTubeEvent, type YouTubePlayer as YoutubePlayerType } from 'react-youtube'
import {
  BRIGHTCOVE_PLAYER_ID,
  normalizeProviderType,
  useResolvedVideoPlayer,
  VideoProviderType,
  type VideoProviderData,
} from '@fs/zion-rootstech'
import WebinarPlayer from '../WebinarPlayer'
import ErrorPlaceholder from '../ErrorPlaceholder'
import VideoCountdown from '../VideoCountdown'
import VideoPlayerFallback from '../VideoPlayerFallback'
import { useWatchEvent, useError } from './hooks'
import { second } from '../../../config'
import { getNowTimestamp } from '../../helpers/dateTimeHelpers'

const YoutubePlayer = lazy(() => import('../YoutubePlayer'))
const BrightcovePlayer = lazy(() => import('../BrightcovePlayer'))

type VideoProviderProps = {
  autoplay?: boolean
  videoProvider?: VideoProviderData
  seekStartTime?: number
  title?: string
  thumbnail?: string
  disableControls?: boolean
  onEnd?: () => void
  loading?: boolean
  countdownStartTime?: number
  isLive?: boolean
  sessionId?: string
  contentLocale?: string
  videoStartTime?: number
  videoDuration?: number | string
}

const LoadingVideo = (): JSX.Element => {
  return (
    <div style={{ aspectRatio: '16 / 9' }}>
      <Skeleton.Image height="100%" width="100%" />
    </div>
  )
}

export default function VideoProvider({
  autoplay,
  videoProvider,
  seekStartTime,
  title,
  thumbnail,
  disableControls,
  onEnd,
  loading,
  countdownStartTime,
  isLive: isLiveProp,
  sessionId,
  contentLocale,
  videoStartTime,
  videoDuration,
}: VideoProviderProps): JSX.Element {
  const [isError, onError, reportError] = useError(videoProvider) as [
    boolean | undefined,
    (args: { error?: unknown; message?: string }) => void,
    (args: { error?: unknown; message?: string }) => void,
  ]

  const resolvedPlayer = useResolvedVideoPlayer(
    { videoProvider, videoStartTime, videoDuration, countdownStartTime, isLive: isLiveProp },
    { getNow: getNowTimestamp }
  )

  const errorReason = resolvedPlayer.kind === 'error' ? resolvedPlayer.reason : undefined

  useEffect(() => {
    if (errorReason) reportError({ message: errorReason })
  }, [errorReason, reportError])

  const videoProviderType = normalizeProviderType(videoProvider?.providerType) as VideoProviderType | undefined

  const { trackHistoryEvent, setVideoPlayer } = useWatchEvent({
    videoProviderType,
    sessionId,
    startTime: videoStartTime ? videoStartTime / second : 0,
  })

  const { signedIn } = useUser()
  const refFirstTimePlayed = useRef(false)

  // If this is the first time the video is played after page render,
  // the user is signed in, and the seekStartTime is greater than 0,
  // then track the "video_seek" analytics event.
  const onPlay = useCallback(
    (obj?: BrightcovePlayerType | YouTubeEvent<number>) => {
      if (obj && refFirstTimePlayed.current === false) {
        refFirstTimePlayed.current = true
        // BrightcovePlayer passes the Video.js player directly to onStart; YouTube wraps it in an event object
        const player: BrightcovePlayerType | YoutubePlayerType =
          videoProviderType === VideoProviderType.Brightcove
            ? (obj as BrightcovePlayerType)
            : (obj as YouTubeEvent<number>).target
        setVideoPlayer(player)
        if (sessionId && signedIn && seekStartTime && seekStartTime > 0) {
          trackLink({ name: `video_seek: ${title?.substring(0, 20)}` })
        }
      }
    },
    [seekStartTime, sessionId, setVideoPlayer, signedIn, title, videoProviderType]
  )

  const onVideoEnd = useCallback(
    (player?: BrightcovePlayerType | YouTubeEvent<number>) => {
      if (player) trackHistoryEvent(player, true)
      onEnd?.()
    },
    [trackHistoryEvent, onEnd]
  )

  const onVideoPause = useCallback(
    (player?: BrightcovePlayerType | YouTubeEvent<number>) => {
      if (player) trackHistoryEvent(player, false)
    },
    [trackHistoryEvent]
  )

  const onBrightcoveError = useCallback(
    (mediaError?: MediaError) => {
      onError({ error: mediaError, message: `Internal error from the Brightcove player: ${title}` })
    },
    [onError, title]
  )

  const onYoutubeError = useCallback(
    (error: unknown) => {
      // eslint-disable-next-line no-console -- this is a valid use
      console.info(
        'Check the Youtube Player API - onError docs for more details: https://developers.google.com/youtube/iframe_api_reference#Events'
      )
      onError({ error, message: `Internal error from the Youtube player: ${title}` })
    },
    [onError, title]
  )

  if (loading) {
    return <LoadingVideo />
  }
  if (isError) {
    return <ErrorPlaceholder />
  }

  switch (resolvedPlayer.kind) {
    case 'brightcove':
      return (
        <div data-testid="video-player">
          <Suspense fallback={<span />}>
            <BrightcovePlayer
              autoplay={autoplay}
              start={seekStartTime}
              contentLocale={contentLocale}
              videoId={resolvedPlayer.videoId}
              accountId={resolvedPlayer.accountId}
              playerId={BRIGHTCOVE_PLAYER_ID}
              title={title}
              posterOverride={thumbnail}
              onError={onBrightcoveError}
              onEnd={onVideoEnd}
              onPause={onVideoPause}
              onStart={onPlay}
            />
          </Suspense>
        </div>
      )
    case 'youtube':
      return (
        <div data-testid="video-player">
          <Suspense fallback={<span />}>
            <YoutubePlayer
              videoId={resolvedPlayer.videoId}
              title={title}
              autoplay={autoplay}
              start={seekStartTime}
              disableControls={disableControls}
              onError={onYoutubeError}
              onEnd={onVideoEnd}
              onPause={onVideoPause}
              isLive={resolvedPlayer.isLive}
              onPlay={onPlay}
            />
          </Suspense>
        </div>
      )
    case 'webinar':
      return <WebinarPlayer thumbnail={thumbnail} url={resolvedPlayer.url} />
    case 'countdown':
      return <VideoCountdown startTimestamp={resolvedPlayer.startTimestamp} />
    case 'error':
      return <ErrorPlaceholder />
    default:
      return (
        <VideoPlayerFallback>
          <Trans i18nKey="home.video-fallback.heading" defaults="You can browse videos in the on-demand section" />
        </VideoPlayerFallback>
      )
  }
}
